import React, { useState } from 'react';
import { CreateFormFeatureDialog, MultilineTextField } from 'components';
import { Controller, useForm } from 'react-hook-form';
import {
    createOrUpdateSmartContractStyles,
    createOrUpdateSmartContractDefaults,
    CreateOrUpdateSmartContractFormData,
    CreateOrUpdateSmartContractSchema,
    smartContractConfig,
} from './create-or-update-smart-contract-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';
import { Box, Grid } from '@mui/material';
import { ErrorMessage, FormLabel, TextField } from '@biq/apex-ui';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { CodeMirrorStyles } from 'lib';
import { useCreateSmartContract } from 'hooks';
import { useAuth } from '../../../core';

export const CreateSmartContract = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const chain = queryParams.get('chain');
    const { mutate: createSmartContract } = useCreateSmartContract();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [deserializedCode, setDeserializedCode] = useState<
        string | undefined
    >();
    const {
        handleSubmit,
        control,
        setValue,
        clearErrors,
        formState: { errors },
    } = useForm<CreateOrUpdateSmartContractFormData>({
        mode: 'all',
        resolver: yupResolver(CreateOrUpdateSmartContractSchema),
        defaultValues: createOrUpdateSmartContractDefaults,
    });

    const onSubmit = (data: CreateOrUpdateSmartContractFormData): void => {
        setIsLoading(true);
        createSmartContract(
            {
                smartContract: {
                    name: data.name,
                    description: data.description,
                    javaScript: deserializedCode as string,
                },
                chain: chain as string,
                token: getToken(),
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, {
                        state: chain,
                    });
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleCloseOrBackButtonClick = (): void => {
        navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, { state: chain });
    };

    const onCodeChange = React.useCallback((value: string) => {
        setDeserializedCode(value);
        setValue('javaScript', value);
        clearErrors('javaScript');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={handleSubmit(onSubmit)}
            isLoading={isLoading}
            handleCloseButton={handleCloseOrBackButtonClick}
            handleBackButton={handleCloseOrBackButtonClick}
            listConfig={smartContractConfig}
        >
            <Grid sx={createOrUpdateSmartContractStyles.containerStyles}>
                <Grid
                    sx={
                        createOrUpdateSmartContractStyles.textFieldContainerStyles
                    }
                >
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Name'
                                required
                                error={!!errors.name}
                                errorText={
                                    errors.name ? errors.name.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={createOrUpdateSmartContractStyles.gridContainerStyles}
                >
                    <MultilineTextField
                        control={control}
                        label='Description'
                        name='description'
                        error={!!errors.description}
                        errorText={
                            errors.description ? errors.description.message : ''
                        }
                    />
                </Grid>
                <Grid>
                    <Grid
                        sx={
                            createOrUpdateSmartContractStyles.gridContainerStyles
                        }
                    >
                        <Controller
                            name='javaScript'
                            control={control}
                            render={(): React.JSX.Element => (
                                <>
                                    <FormLabel
                                        required
                                        label='Smart Contract'
                                    />
                                    <CodeMirror
                                        value={deserializedCode}
                                        style={{
                                            ...(errors?.javaScript
                                                ? createOrUpdateSmartContractStyles.javaScriptError
                                                : createOrUpdateSmartContractStyles.javaScript),
                                        }}
                                        height={
                                            createOrUpdateSmartContractStyles.codeMirrorHeight
                                        }
                                        extensions={[
                                            javascript({ jsx: true }),
                                            CodeMirrorStyles,
                                        ]}
                                        onChange={onCodeChange}
                                    />
                                    {errors?.javaScript && (
                                        <Box
                                            style={
                                                createOrUpdateSmartContractStyles.javaScriptErrorContainer
                                            }
                                        >
                                            <ErrorMessage
                                                errorMessage={
                                                    errors?.javaScript
                                                        .message ?? ''
                                                }
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </CreateFormFeatureDialog>
    );
};
